import { patch } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { logEvent } from "@src/appV2/lib/analytics";
import { IN_HOME_USER_EVENTS } from "@src/appV2/redesign/InHome/lib/events";
import { getVisitQueryKey } from "@src/appV2/redesign/InHome/useGetVisitData";
import { getVisitsQueryKey } from "@src/appV2/redesign/InHome/useGetVisitsWithCaseData";
import { getVisitInvitesQueryKey } from "@src/appV2/redesign/InHome/VisitInvites/api/useWorkerPendingVisitInvites";
import { useMutation, type UseMutationOptions, useQueryClient } from "@tanstack/react-query";

import {
  type BookVisitRequest,
  bookVisitRequestSchema,
  visitResponseSchema,
  type VisitWithCaseData,
} from "../types";

export const BOOK_VISIT_URL = `${environmentConfig.REACT_APP_HOME_HEALTH_API_URL}/v1/visits/:visitId`;

export function useBookVisit(
  visitId: string,
  options: UseMutationOptions<VisitWithCaseData, string, BookVisitRequest> = {}
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params) => {
      const response = await patch({
        url: BOOK_VISIT_URL.replace(":visitId", visitId),
        data: params,
        requestSchema: bookVisitRequestSchema,
        responseSchema: visitResponseSchema,
      });
      return response.data.data;
    },
    onSuccess: (visit) => {
      logEvent(IN_HOME_USER_EVENTS.VISIT_BOOKED, {
        caseId: visit.attributes.caseId,
        visitId: visit.id,
        workerReq: visit.attributes.workerReq,
        type: visit.attributes.type,
      });
      void queryClient.invalidateQueries({ queryKey: getVisitsQueryKey });
      void queryClient.invalidateQueries({
        queryKey: getVisitQueryKey(visit.id, visit.attributes.caseId),
      });
      void queryClient.invalidateQueries({ queryKey: getVisitInvitesQueryKey });
    },
    ...options,
  });
}
